import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { adjustBalance } from "../services/loyaltyService";
import { labelFor } from "../services/enumLabels";

/** The reasons a person may give for moving a balance by hand. */
const REASONS = ["goodwill", "correction", "manual_adjustment"];

/**
 * Manual adjust (`loyalty.adjust`): add or take away points, a reason is
 * required, and a second step confirms exactly what will be written to the
 * ledger. The server refuses to take more than the client holds.
 */
export default function LoyaltyAdjustModal({ open, clientId, clientName, balance, onClose, onDone }) {
  const { t } = useTranslation();
  const [direction, setDirection] = useState("add");
  const [points, setPoints] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setDirection("add");
    setPoints("");
    setReason("");
    setNote("");
    setConfirming(false);
    setError("");
    setFieldErrors({});
  }, [open]);

  if (!open) return null;

  const amount = Math.trunc(Number(points) || 0);

  const review = (e) => {
    e.preventDefault();
    const missing = {};
    if (amount <= 0) missing.points = [t("loyalty.adjust.pointsRequired")];
    if (!reason) missing.reason = [t("loyalty.adjust.reasonRequired")];
    setFieldErrors(missing);
    if (Object.keys(missing).length === 0) setConfirming(true);
  };

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await adjustBalance(clientId, {
        points: direction === "add" ? amount : -amount,
        reason,
        note: note.trim(),
      });
      onDone();
      onClose();
    } catch (err) {
      setFieldErrors(apiFieldErrors(err));
      setError(apiErrorMessage(err, t("loyalty.adjust.failed")));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4"
      onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="la-title"
        className="card my-12 w-full max-w-md p-5"
      >
        <h2 id="la-title" className="text-lg font-bold">
          {t("loyalty.adjust.title")}
        </h2>
        <p className="mb-4 text-sm text-ink-2">
          {t("loyalty.adjust.subtitle", { name: clientName, balance })}
        </p>

        {error && (
          <div
            className="mb-3 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text"
            role="alert"
          >
            {error}
          </div>
        )}

        {confirming ? (
          <div>
            <p className="rounded-lg bg-raised px-3 py-3 text-sm">
              {t(direction === "add" ? "loyalty.adjust.confirmAdd" : "loyalty.adjust.confirmRemove", {
                points: amount,
                name: clientName,
                reason: labelFor("loyalty_reason", reason),
              })}
            </p>
            {note.trim() && <p className="mt-2 text-sm text-ink-2">“{note.trim()}”</p>}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setConfirming(false)}
                disabled={busy}
              >
                {t("common.back")}
              </button>
              <button type="button" className="btn-primary" onClick={submit} disabled={busy}>
                {busy ? t("common.saving") : t("loyalty.adjust.confirm")}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={review} noValidate className="space-y-4">
            <div
              role="radiogroup"
              aria-label={t("loyalty.adjust.direction")}
              className="grid grid-cols-2 gap-2"
            >
              {["add", "remove"].map((value) => (
                <label
                  key={value}
                  className={`flex min-h-touch cursor-pointer items-center justify-center rounded-input border px-3 text-sm ${
                    direction === value ? "border-accent bg-accent-wash font-semibold" : "border-line-strong"
                  }`}
                >
                  <input
                    type="radio"
                    name="direction"
                    className="sr-only"
                    checked={direction === value}
                    onChange={() => setDirection(value)}
                  />
                  {t(`loyalty.adjust.${value}`)}
                </label>
              ))}
            </div>

            <div>
              <label className="field-label" htmlFor="la-points">
                {t("loyalty.adjust.points")} <span className="text-danger-text">*</span>
              </label>
              <input
                id="la-points"
                type="number"
                min="1"
                dir="ltr"
                className={`field-input ${fieldErrors.points ? "border-danger" : ""}`}
                value={points}
                onChange={(e) => setPoints(e.target.value)}
              />
              {fieldErrors.points && <p className="field-error">{fieldErrors.points[0]}</p>}
            </div>

            <div>
              <label className="field-label" htmlFor="la-reason">
                {t("loyalty.adjust.reason")} <span className="text-danger-text">*</span>
              </label>
              <select
                id="la-reason"
                className={`field-input ${fieldErrors.reason ? "border-danger" : ""}`}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              >
                <option value="">{t("loyalty.adjust.chooseReason")}</option>
                {REASONS.map((value) => (
                  <option key={value} value={value}>
                    {labelFor("loyalty_reason", value)}
                  </option>
                ))}
              </select>
              {fieldErrors.reason && <p className="field-error">{fieldErrors.reason[0]}</p>}
            </div>

            <div>
              <label className="field-label" htmlFor="la-note">
                {t("loyalty.adjust.note")}
              </label>
              <textarea
                id="la-note"
                rows={2}
                maxLength={1000}
                className="field-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>

            <div className="flex justify-end gap-2">
              <button type="button" className="btn-secondary" onClick={onClose}>
                {t("common.cancel")}
              </button>
              <button type="submit" className="btn-primary">
                {t("loyalty.adjust.review")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
